import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Loader2, ArrowLeft, Users, DollarSign, TrendingUp, Wallet,
  Copy, CheckCircle, Clock, FileText, Upload, CreditCard,
  Building, Mail, Bitcoin, AlertCircle, Gift, RefreshCw,
  Shield, ChevronRight, ExternalLink, Activity, BarChart3,
  Megaphone, UserPlus, ShoppingCart, Link2, Image, Type,
  Code
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component
const StatCard = ({ title, value, subValue, icon: Icon, color = 'blue' }) => {
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-500/10',
    green: 'text-green-400 bg-green-500/10',
    cyan: 'text-cyan-400 bg-cyan-500/10',
    yellow: 'text-yellow-400 bg-yellow-500/10',
  };

  return (
    <Card className="bg-[hsl(220,20%,7%)] border-white/10">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-400 mb-1">{title}</p>
            <p className="text-2xl font-bold text-white font-mono">{value}</p>
            {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AffiliatePortal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState(null);
  const [hasAccount, setHasAccount] = useState(false);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Application form state
  const [applying, setApplying] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [bankDetails, setBankDetails] = useState({
    bank_name: '',
    account_number: '',
    account_holder_name: '',
    routing_number: '',
    paypal_email: '',
    crypto_wallet: '',
    crypto_network: 'TRC20'
  });
  
  // Payout request state
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  
  // Document upload state
  const [uploading, setUploading] = useState(false);
  
  // Enhanced dashboard state
  const [activities, setActivities] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('30d');
  const [materials, setMaterials] = useState(null);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  useEffect(() => {
    fetchAffiliateData();
  }, []);

  const fetchAffiliateData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Get affiliate account
      const accountRes = await fetch(`${API_URL}/api/affiliates/me`, { headers });
      const accountData = await accountRes.json();
      
      setHasAccount(accountData.has_affiliate_account);
      setAffiliate(accountData.affiliate);
      
      if (accountData.has_affiliate_account && accountData.affiliate?.status === 'approved') {
        // Fetch stats, referrals, payouts
        const [statsRes, referralsRes, payoutsRes] = await Promise.all([
          fetch(`${API_URL}/api/affiliates/stats`, { headers }),
          fetch(`${API_URL}/api/affiliates/referrals`, { headers }),
          fetch(`${API_URL}/api/affiliates/payouts`, { headers })
        ]);
        
        const statsData = await statsRes.json();
        const referralsData = await referralsRes.json();
        const payoutsData = await payoutsRes.json();
        
        setStats(statsData.stats);
        setReferrals(referralsData.referrals || []);
        setPayouts(payoutsData.payouts || []);
      }
    } catch (error) {
      console.error('Failed to fetch affiliate data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    setLoadingActivities(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/affiliates/activity?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActivities(data.activities || []);
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoadingActivities(false);
    }
  };

  const fetchAnalytics = async (period = analyticsPeriod) => {
    setLoadingAnalytics(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/affiliates/analytics?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchMaterials = async () => {
    setLoadingMaterials(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/affiliates/marketing-materials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMaterials(data);
      }
    } catch (error) {
      console.error('Failed to fetch materials:', error);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setApplying(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/affiliates/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          custom_code: customCode || null,
          payment_method: paymentMethod,
          bank_name: bankDetails.bank_name,
          account_number: bankDetails.account_number,
          account_holder_name: bankDetails.account_holder_name,
          routing_number: bankDetails.routing_number,
          paypal_email: bankDetails.paypal_email
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Application submitted! Awaiting admin approval.');
        setHasAccount(true);
        setAffiliate(data.affiliate);
      } else {
        toast.error(data.detail || 'Application failed');
      }
    } catch (error) {
      toast.error('Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(affiliate?.referral_code || '');
    toast.success('Referral code copied!');
  };

  // Load tab data lazily
  useEffect(() => {
    if (!affiliate || affiliate.status !== 'approved') return;
    if (activeTab === 'activity' && activities.length === 0) fetchActivities();
    if (activeTab === 'analytics' && !analytics) fetchAnalytics();
    if (activeTab === 'materials' && !materials) fetchMaterials();
  }, [activeTab, affiliate]);

  // Refetch analytics when period changes
  useEffect(() => {
    if (activeTab === 'analytics' && affiliate?.status === 'approved') {
      fetchAnalytics(analyticsPeriod);
    }
  }, [analyticsPeriod]);

  const handleRequestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount < 500) {
      toast.error('Minimum payout is $500');
      return;
    }
    
    setRequestingPayout(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/affiliates/payouts/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: amount,
          payment_method: affiliate?.payment_details?.method || 'bank'
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Payout requested successfully!');
        setPayoutAmount('');
        fetchAffiliateData();
      } else {
        toast.error(data.detail || 'Payout request failed');
      }
    } catch (error) {
      toast.error('Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      
      const response = await fetch(`${API_URL}/api/affiliates/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Document uploaded successfully!');
        fetchAffiliateData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,4%)]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  // Application Form (No affiliate account yet)
  if (!hasAccount) {
    return (
      <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20" data-testid="affiliate-apply">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="mb-6 border-white/10 text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-white">Become an Affiliate</CardTitle>
                <CardDescription className="text-gray-400">
                  Earn 5% commission on every first-time referral purchase
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleApply} className="space-y-6">
                  {/* Referral Code */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Referral Code (Optional)</Label>
                    <Input
                      placeholder="Leave blank for auto-generated code"
                      value={customCode}
                      onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                      className="bg-white/5 border-white/10 text-white"
                    />
                    <p className="text-xs text-gray-500">
                      Create your own code (4-20 characters) or get one auto-generated
                    </p>
                  </div>

                  {/* Payment Method */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Preferred Payment Method</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(220,20%,7%)] border-white/10">
                        <SelectItem value="bank" className="text-white">Bank Transfer</SelectItem>
                        <SelectItem value="paypal" className="text-white">PayPal</SelectItem>
                        <SelectItem value="crypto" className="text-white">Cryptocurrency</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Bank Details */}
                  {paymentMethod === 'bank' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-white/10">
                      <h4 className="font-medium text-white flex items-center gap-2">
                        <Building className="w-4 h-4 text-cyan-400" />
                        Bank Details
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Bank Name</Label>
                          <Input
                            value={bankDetails.bank_name}
                            onChange={(e) => setBankDetails({...bankDetails, bank_name: e.target.value})}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Account Holder</Label>
                          <Input
                            value={bankDetails.account_holder_name}
                            onChange={(e) => setBankDetails({...bankDetails, account_holder_name: e.target.value})}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Account Number</Label>
                          <Input
                            value={bankDetails.account_number}
                            onChange={(e) => setBankDetails({...bankDetails, account_number: e.target.value})}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Routing Number</Label>
                          <Input
                            value={bankDetails.routing_number}
                            onChange={(e) => setBankDetails({...bankDetails, routing_number: e.target.value})}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {paymentMethod === 'paypal' && (
                    <div className="space-y-2">
                      <Label className="text-gray-300">PayPal Email</Label>
                      <Input
                        type="email"
                        value={bankDetails.paypal_email}
                        onChange={(e) => setBankDetails({...bankDetails, paypal_email: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="your@email.com"
                      />
                    </div>
                  )}

                  {paymentMethod === 'crypto' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-white/10">
                      <h4 className="font-medium text-white flex items-center gap-2">
                        <Bitcoin className="w-4 h-4 text-yellow-400" />
                        Cryptocurrency Details
                      </h4>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Network</Label>
                          <Select 
                            value={bankDetails.crypto_network} 
                            onValueChange={(value) => setBankDetails({...bankDetails, crypto_network: value})}
                          >
                            <SelectTrigger className="bg-white/5 border-white/10 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[hsl(220,20%,7%)] border-white/10">
                              <SelectItem value="TRC20" className="text-white">USDT (TRC20 - Tron)</SelectItem>
                              <SelectItem value="ERC20" className="text-white">USDT (ERC20 - Ethereum)</SelectItem>
                              <SelectItem value="BEP20" className="text-white">USDT (BEP20 - BSC)</SelectItem>
                              <SelectItem value="BTC" className="text-white">Bitcoin (BTC)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Wallet Address</Label>
                          <Input
                            value={bankDetails.crypto_wallet}
                            onChange={(e) => setBankDetails({...bankDetails, crypto_wallet: e.target.value})}
                            className="bg-white/5 border-white/10 text-white font-mono text-sm"
                            placeholder="Enter your wallet address"
                          />
                          <p className="text-xs text-yellow-400">
                            ⚠️ Double-check your address. Funds sent to wrong address cannot be recovered.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    disabled={applying}
                    className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                  >
                    {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
                    Apply to Become an Affiliate
                  </Button>
                </form>

                {/* Benefits */}
                <div className="mt-8 p-4 rounded-lg bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-cyan-500/20">
                  <h4 className="font-medium text-white mb-3">Affiliate Benefits</h4>
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      5% commission on every first-time referral purchase
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      Unique referral code for tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      Real-time earnings dashboard
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      Fast payouts (minimum $500)
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Pending Approval State
  if (affiliate?.status === 'pending') {
    return (
      <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20" data-testid="affiliate-pending">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="mb-6 border-white/10 text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <Card className="bg-[hsl(220,20%,7%)] border-white/10">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-10 h-10 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Application Pending</h2>
              <p className="text-gray-400 mb-6">
                Your affiliate application is being reviewed. You'll be notified once approved.
              </p>
              <div className="p-4 rounded-lg bg-white/5 inline-block">
                <p className="text-gray-400 text-sm">Your Referral Code (once approved)</p>
                <p className="text-xl font-mono font-bold text-cyan-400">{affiliate.referral_code}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main Affiliate Dashboard (Approved)
  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20" data-testid="affiliate-portal">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
        >
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="border-white/10 text-white hover:bg-white/5"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div>
              <h1 className="font-heading text-3xl font-bold text-white">Affiliate Portal</h1>
              <p className="text-gray-400 text-sm">Manage your referrals and earnings</p>
            </div>
          </div>
          
          {/* Referral Code */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-cyan-500/30">
            <div>
              <p className="text-xs text-gray-400">Your Referral Code</p>
              <p className="font-mono font-bold text-cyan-400">{affiliate?.referral_code}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyCode}
              className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <StatCard 
            title="Total Earnings" 
            value={formatCurrency(stats?.total_earnings)} 
            icon={DollarSign} 
            color="green" 
          />
          <StatCard 
            title="Pending Balance" 
            value={formatCurrency(stats?.pending_balance)} 
            subValue="Available for payout"
            icon={Wallet} 
            color="cyan" 
          />
          <StatCard 
            title="Total Paid Out" 
            value={formatCurrency(stats?.paid_out)} 
            icon={CheckCircle} 
            color="blue" 
          />
          <StatCard 
            title="Successful Referrals" 
            value={stats?.successful_referrals || 0} 
            subValue={`${stats?.total_referrals || 0} total signups`}
            icon={Users} 
            color="yellow" 
          />
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[hsl(220,20%,7%)] border border-white/10 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              Activity
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              Analytics
            </TabsTrigger>
            <TabsTrigger value="referrals" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              Referrals
            </TabsTrigger>
            <TabsTrigger value="payouts" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              Payouts
            </TabsTrigger>
            <TabsTrigger value="materials" data-testid="tab-materials" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              Marketing
            </TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              Documents
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Request Payout */}
              <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-cyan-400" />
                    Request Payout
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 rounded-lg bg-white/5">
                    <p className="text-gray-400 text-sm">Available Balance</p>
                    <p className="text-3xl font-bold font-mono text-white">{formatCurrency(stats?.pending_balance)}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400">Amount to Withdraw</Label>
                    <Input
                      type="number"
                      placeholder="Min. $500"
                      value={payoutAmount}
                      onChange={(e) => setPayoutAmount(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <Button
                    onClick={handleRequestPayout}
                    disabled={requestingPayout || (stats?.pending_balance || 0) < 500}
                    className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                  >
                    {requestingPayout ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Request Payout
                  </Button>
                  <p className="text-xs text-gray-500 text-center">Minimum payout: $500</p>
                </CardContent>
              </Card>

              {/* Recent Referrals */}
              <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-cyan-400" />
                    Recent Referrals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {referrals.length > 0 ? (
                    <div className="space-y-3">
                      {referrals.slice(0, 5).map((ref) => (
                        <div key={ref.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                          <div>
                            <p className="text-white font-medium">{ref.referred_user_email}</p>
                            <p className="text-xs text-gray-500">{formatDate(ref.created_at)}</p>
                          </div>
                          <div className="text-right">
                            <Badge className={ref.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                              {ref.status}
                            </Badge>
                            {ref.commission_amount > 0 && (
                              <p className="text-green-400 font-mono text-sm mt-1">+{formatCurrency(ref.commission_amount)}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No referrals yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Share Link */}
            <Card className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-cyan-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="font-bold text-white mb-1">Share Your Referral Link</h3>
                    <p className="text-gray-400 text-sm">
                      Share this link with potential traders to earn commissions
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="px-3 py-2 rounded bg-black/30 text-cyan-400 text-sm">
                      traderscapital.net/?ref={affiliate?.referral_code}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(`https://traderscapital.net/?ref=${affiliate?.referral_code}`);
                        toast.success('Link copied!');
                      }}
                      className="border-cyan-500/30 text-cyan-400"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Feed Tab */}
          <TabsContent value="activity" data-testid="activity-tab-content">
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-cyan-400" />
                  Activity Feed
                </CardTitle>
                <Button variant="outline" size="sm" onClick={fetchActivities} disabled={loadingActivities}
                  className="border-white/10 text-white hover:bg-white/5" data-testid="refresh-activity-btn">
                  <RefreshCw className={`w-4 h-4 mr-1 ${loadingActivities ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {loadingActivities ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
                ) : activities.length > 0 ? (
                  <div className="space-y-3">
                    {activities.map((act, idx) => {
                      const iconMap = { signup: <UserPlus className="w-4 h-4" />, purchase: <ShoppingCart className="w-4 h-4" />, commission: <DollarSign className="w-4 h-4" />, payout: <Wallet className="w-4 h-4" /> };
                      const colorMap = { signup: 'text-blue-400 bg-blue-500/10', purchase: 'text-green-400 bg-green-500/10', commission: 'text-yellow-400 bg-yellow-500/10', payout: 'text-cyan-400 bg-cyan-500/10' };
                      return (
                        <div key={act.id || idx} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/[0.07] transition-colors" data-testid={`activity-item-${idx}`}>
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${colorMap[act.type] || 'text-gray-400 bg-gray-500/10'}`}>
                            {iconMap[act.type] || <Activity className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium">{act.message}</p>
                            {act.amount > 0 && <p className="text-green-400 font-mono text-sm">+{formatCurrency(act.amount)}</p>}
                            {act.commission > 0 && <p className="text-yellow-400 font-mono text-xs">Commission: {formatCurrency(act.commission)}</p>}
                            {act.status && <Badge className="mt-1 bg-white/10 text-gray-300 text-xs">{act.status}</Badge>}
                          </div>
                          <span className="text-xs text-gray-500 shrink-0">{act.timestamp ? new Date(act.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Activity className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500">No activity yet. Share your referral code to get started!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" data-testid="analytics-tab-content">
            <div className="space-y-6">
              {/* Period Selector */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-cyan-400" /> Performance Analytics
                </h2>
                <Select value={analyticsPeriod} onValueChange={setAnalyticsPeriod}>
                  <SelectTrigger className="w-[140px] bg-white/5 border-white/10 text-white" data-testid="analytics-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(220,20%,7%)] border-white/10">
                    <SelectItem value="7d" className="text-white">Last 7 days</SelectItem>
                    <SelectItem value="30d" className="text-white">Last 30 days</SelectItem>
                    <SelectItem value="90d" className="text-white">Last 90 days</SelectItem>
                    <SelectItem value="all" className="text-white">All time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {loadingAnalytics ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
              ) : analytics ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                      <CardContent className="p-4 text-center">
                        <p className="text-xs text-gray-400 mb-1">Referrals</p>
                        <p className="text-2xl font-bold text-white font-mono" data-testid="analytics-referrals">{analytics.summary?.total_referrals || 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                      <CardContent className="p-4 text-center">
                        <p className="text-xs text-gray-400 mb-1">Conversions</p>
                        <p className="text-2xl font-bold text-green-400 font-mono" data-testid="analytics-conversions">{analytics.summary?.conversions || 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                      <CardContent className="p-4 text-center">
                        <p className="text-xs text-gray-400 mb-1">Conv. Rate</p>
                        <p className="text-2xl font-bold text-cyan-400 font-mono" data-testid="analytics-conv-rate">{analytics.summary?.conversion_rate || 0}%</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                      <CardContent className="p-4 text-center">
                        <p className="text-xs text-gray-400 mb-1">Earnings</p>
                        <p className="text-2xl font-bold text-yellow-400 font-mono" data-testid="analytics-earnings">{formatCurrency(analytics.summary?.total_earnings)}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                      <CardContent className="p-4 text-center">
                        <p className="text-xs text-gray-400 mb-1">Avg Commission</p>
                        <p className="text-2xl font-bold text-white font-mono" data-testid="analytics-avg-commission">{formatCurrency(analytics.summary?.avg_commission)}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Daily Chart (simple bar representation) */}
                  {analytics.daily_data && analytics.daily_data.length > 0 && (
                    <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white text-base">Daily Referrals</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-end gap-[2px] h-32 overflow-x-auto" data-testid="daily-chart">
                          {analytics.daily_data.map((day, i) => {
                            const maxRef = Math.max(...analytics.daily_data.map(d => d.referrals), 1);
                            const height = day.referrals > 0 ? Math.max((day.referrals / maxRef) * 100, 8) : 4;
                            return (
                              <div key={i} className="flex flex-col items-center flex-1 min-w-[8px] group relative">
                                <div className="absolute -top-8 hidden group-hover:block bg-black/80 text-xs text-white px-2 py-1 rounded whitespace-nowrap z-10">
                                  {day.date}: {day.referrals} referrals{day.earnings > 0 ? `, ${formatCurrency(day.earnings)}` : ''}
                                </div>
                                <div
                                  className={`w-full rounded-t transition-all ${day.referrals > 0 ? 'bg-cyan-500/70 hover:bg-cyan-400' : 'bg-white/5'}`}
                                  style={{ height: `${height}%` }}
                                />
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-between mt-2 text-xs text-gray-500">
                          <span>{analytics.daily_data[0]?.date}</span>
                          <span>{analytics.daily_data[analytics.daily_data.length - 1]?.date}</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Top Referrals */}
                  {analytics.top_referrals && analytics.top_referrals.length > 0 && (
                    <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white text-base">Top Performing Referrals</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {analytics.top_referrals.map((ref, i) => (
                            <div key={ref.id || i} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-mono text-gray-500 w-5">{i + 1}.</span>
                                <span className="text-white text-sm">{ref.referred_user_email || 'Referral'}</span>
                              </div>
                              <div className="text-right">
                                <p className="text-white font-mono text-sm">{formatCurrency(ref.purchase_amount)}</p>
                                <p className="text-green-400 font-mono text-xs">+{formatCurrency(ref.commission_amount)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500">No analytics data available yet.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">All Referrals</CardTitle>
              </CardHeader>
              <CardContent>
                {referrals.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Email</th>
                          <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Date</th>
                          <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Status</th>
                          <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Purchase</th>
                          <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referrals.map((ref) => (
                          <tr key={ref.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 px-4 text-white">{ref.referred_user_email}</td>
                            <td className="py-3 px-4 text-gray-400">{formatDate(ref.created_at)}</td>
                            <td className="py-3 px-4">
                              <Badge className={ref.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                                {ref.status}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-white font-mono">{ref.purchase_amount > 0 ? formatCurrency(ref.purchase_amount) : '-'}</td>
                            <td className="py-3 px-4 text-green-400 font-mono">{ref.commission_amount > 0 ? `+${formatCurrency(ref.commission_amount)}` : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-12">No referrals yet. Share your code to start earning!</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Payout History</CardTitle>
              </CardHeader>
              <CardContent>
                {payouts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Date</th>
                          <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Amount</th>
                          <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Method</th>
                          <th className="py-3 px-4 text-left text-sm font-medium text-gray-400">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payouts.map((payout) => (
                          <tr key={payout.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 px-4 text-gray-400">{formatDate(payout.created_at)}</td>
                            <td className="py-3 px-4 text-white font-mono font-bold">{formatCurrency(payout.amount)}</td>
                            <td className="py-3 px-4 text-gray-400 capitalize">{payout.payment_method}</td>
                            <td className="py-3 px-4">
                              <Badge className={
                                payout.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                payout.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                payout.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                'bg-blue-500/20 text-blue-400'
                              }>
                                {payout.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-12">No payout history</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Marketing Materials Tab */}
          <TabsContent value="materials" data-testid="materials-tab-content">
            {loadingMaterials ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
            ) : materials ? (
              <div className="space-y-6">
                {/* Referral Links */}
                <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Link2 className="w-5 h-5 text-cyan-400" /> Referral Links
                    </CardTitle>
                    <CardDescription className="text-gray-400">Share these links to earn commissions on every purchase</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {materials.links?.map((link, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10 gap-3" data-testid={`material-link-${i}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium">{link.name}</p>
                          <p className="text-xs text-gray-500 truncate">{link.description}</p>
                          <code className="text-xs text-cyan-400 font-mono block truncate mt-1">{link.url}</code>
                        </div>
                        <Button variant="outline" size="sm" className="border-white/10 text-white shrink-0"
                          onClick={() => { navigator.clipboard.writeText(link.url); toast.success(`${link.name} link copied!`); }}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Promotional Copy */}
                <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Type className="w-5 h-5 text-cyan-400" /> Promotional Copy
                    </CardTitle>
                    <CardDescription className="text-gray-400">Ready-to-use text for emails and social media</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {materials.promotional_copy?.map((copy, i) => (
                      <div key={i} className="p-4 rounded-lg bg-white/5 border border-white/10" data-testid={`promo-copy-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white text-sm font-medium">{copy.name}</h4>
                          <Button variant="outline" size="sm" className="border-white/10 text-white"
                            onClick={() => { navigator.clipboard.writeText(copy.text); toast.success(`${copy.name} copied!`); }}>
                            <Copy className="w-3 h-3 mr-1" /> Copy
                          </Button>
                        </div>
                        <pre className="text-gray-300 text-xs whitespace-pre-wrap font-mono bg-black/30 p-3 rounded max-h-40 overflow-y-auto">{copy.text}</pre>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Banner HTML Embeds */}
                <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Image className="w-5 h-5 text-cyan-400" /> Banner Embeds
                    </CardTitle>
                    <CardDescription className="text-gray-400">Copy HTML code to embed banners on your website or blog</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {materials.banners?.map((banner, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10" data-testid={`banner-${i}`}>
                        <div>
                          <p className="text-white text-sm font-medium">{banner.name}</p>
                          <p className="text-xs text-gray-500">{banner.size}</p>
                        </div>
                        <Button variant="outline" size="sm" className="border-white/10 text-white"
                          onClick={() => { navigator.clipboard.writeText(banner.html); toast.success('Banner HTML copied!'); }}>
                          <Code className="w-3 h-3 mr-1" /> Copy HTML
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Commission Info */}
                {materials.commission_info && (
                  <Card className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-cyan-500/30">
                    <CardContent className="p-6">
                      <h3 className="text-white font-bold mb-3">Commission Details</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400">Rate</p>
                          <p className="text-white font-bold text-lg">{materials.commission_info.rate}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Min Payout</p>
                          <p className="text-white font-bold text-lg">{materials.commission_info.payout_threshold}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-400">Payout Methods</p>
                          <p className="text-white">{materials.commission_info.payout_methods?.join(', ')}</p>
                        </div>
                      </div>
                      <p className="text-gray-400 text-xs mt-3">{materials.commission_info.description}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Megaphone className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">Marketing materials are being prepared.</p>
              </div>
            )}
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">KYC & Contract Documents</CardTitle>
                <CardDescription className="text-gray-400">Upload required documents for verification</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {['contract', 'id_front', 'id_back', 'proof_of_address', 'tax_form'].map((docType) => {
                  const existingDoc = affiliate?.documents?.find(d => d.type === docType);
                  const labels = {
                    contract: 'Signed Affiliate Contract',
                    id_front: 'ID Document (Front)',
                    id_back: 'ID Document (Back)',
                    proof_of_address: 'Proof of Address',
                    tax_form: 'Tax Form (W-9/W-8)'
                  };
                  
                  return (
                    <div key={docType} className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-cyan-400" />
                        <div>
                          <p className="text-white font-medium">{labels[docType]}</p>
                          {existingDoc ? (
                            <p className="text-xs text-gray-500">Uploaded: {formatDate(existingDoc.uploaded_at)}</p>
                          ) : (
                            <p className="text-xs text-gray-500">Not uploaded</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {existingDoc && (
                          <Badge className={existingDoc.status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                            {existingDoc.status}
                          </Badge>
                        )}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="hidden"
                            onChange={(e) => handleDocumentUpload(e, docType)}
                            disabled={uploading}
                          />
                          <Button variant="outline" size="sm" className="border-white/10 text-white" asChild>
                            <span>
                              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                              Upload
                            </span>
                          </Button>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Payment Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-white flex items-center gap-2">
                      <Building className="w-4 h-4 text-cyan-400" />
                      Bank Details
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-gray-400 text-sm">Bank Name</Label>
                        <p className="text-white">{affiliate?.payment_details?.bank_name || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-400 text-sm">Account Holder</Label>
                        <p className="text-white">{affiliate?.payment_details?.account_holder_name || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-400 text-sm">Account Number</Label>
                        <p className="text-white">{affiliate?.payment_details?.account_number ? '****' + affiliate.payment_details.account_number.slice(-4) : '-'}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-medium text-white flex items-center gap-2">
                      <Mail className="w-4 h-4 text-cyan-400" />
                      PayPal
                    </h4>
                    <div>
                      <Label className="text-gray-400 text-sm">PayPal Email</Label>
                      <p className="text-white">{affiliate?.payment_details?.paypal_email || '-'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-white/10">
                  <Button 
                    onClick={() => navigate('/settings')}
                    variant="outline"
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    Update Payment Details
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AffiliatePortal;
